<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { McSelect, McOption, McInputDate, McNumberStepper } from "@maersk-global/mds-components-core";
import { useFiltersStore } from "@/store";
import { useApplyFilter } from "@/features/filters/composables/useApplyFilter/useApplyFilter";
import type { FilterDefinition, FilterType } from "@/features/filters/types";
import { FilterTypeEnum, RelativeDateRangeOption } from "@/features/filters/types";
import {
  clampPlusMinusDays,
  formatRangeHint,
  MAX_PLUS_MINUS_DAYS,
  resolvePlusMinusDays
} from "@/features/filters/utils/relativeDateRange";

const props = defineProps<{
  filter: FilterDefinition;
  filterType: FilterType;
  zonePrefix?: string;
  disabled?: boolean;
}>();

const { t, locale } = useI18n();
const filtersStore = useFiltersStore();
const { filtersApplied, panelSelectedFilters } = filtersStore;
const { applyFilter, removeFilter } = useApplyFilter(props.filterType, props.zonePrefix);

const zone = props.zonePrefix || "default";
const { key: filterKey, label: filterLabel = "", groupId, groupLabel, sectionLabel } = props.filter;

const DEFAULT_OPTIONS = [
  { label: "ORDERS.FILTERS.RELATIVE_DATE.PLUS_MINUS_7", value: RelativeDateRangeOption.PLUS_MINUS_7 },
  { label: "ORDERS.FILTERS.RELATIVE_DATE.PLUS_MINUS_14", value: RelativeDateRangeOption.PLUS_MINUS_14 },
  { label: "ORDERS.FILTERS.RELATIVE_DATE.CUSTOM_DAYS", value: RelativeDateRangeOption.CUSTOM_DAYS },
  { label: "ORDERS.FILTERS.RELATIVE_DATE.SPECIFIC_RANGE", value: RelativeDateRangeOption.SPECIFIC_RANGE }
];
const options = computed(() => (props.filter.options?.length ? props.filter.options : DEFAULT_OPTIONS));

const PRESET_DAYS: Record<string, number> = {
  [RelativeDateRangeOption.PLUS_MINUS_7]: 7,
  [RelativeDateRangeOption.PLUS_MINUS_14]: 14
};

const stored = computed<any>(() => {
  const source = props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied;
  return source?.[zone]?.[filterKey];
});

const selectedOption = ref<string>("");
const customDays = ref<number | "">("");
const fromDate = ref("");
const toDate = ref("");
let selfUpdate = false;

const syncFromStore = (value: any) => {
  if (selfUpdate) return;
  if (!value) {
    selectedOption.value = "";
    customDays.value = "";
    fromDate.value = "";
    toDate.value = "";
    return;
  }
  selectedOption.value = String(value.value ?? "");
  customDays.value = typeof value.days === "number" ? value.days : "";
  fromDate.value = value.dateRange?.startDate ?? "";
  toDate.value = value.dateRange?.endDate ?? "";
};
watch(stored, syncFromStore, { immediate: true, deep: true });

const resolvedRange = computed<{ startDate: string; endDate: string } | null>(() => {
  const option = selectedOption.value;
  if (option in PRESET_DAYS) return resolvePlusMinusDays(new Date(), PRESET_DAYS[option]);
  if (option === RelativeDateRangeOption.CUSTOM_DAYS) {
    return customDays.value === "" ? null : resolvePlusMinusDays(new Date(), Number(customDays.value));
  }
  if (option === RelativeDateRangeOption.SPECIFIC_RANGE && fromDate.value && toDate.value) {
    return { startDate: fromDate.value, endDate: toDate.value };
  }
  return null;
});

const hint = computed(() =>
  resolvedRange.value ? formatRangeHint(resolvedRange.value.startDate, resolvedRange.value.endDate, locale.value) : ""
);

const optionLabel = (value: string) => {
  const found = options.value.find((o) => o.value === value);
  return found ? t(found.label) : value;
};

/** Only a fully resolved range reaches the store; an incomplete one is kept locally and not applied. */
const commit = () => {
  selfUpdate = true;
  try {
    const range = resolvedRange.value;
    if (!range) {
      removeFilter(filterKey);
      return;
    }
    applyFilter(filterKey, {
      filterKey,
      value: selectedOption.value,
      label: formatRangeHint(range.startDate, range.endDate, locale.value) || optionLabel(selectedOption.value),
      dateRange: { ...range },
      ...(selectedOption.value === RelativeDateRangeOption.CUSTOM_DAYS ? { days: Number(customDays.value) } : {}),
      filterType: props.filterType,
      ...(groupId ? { groupId } : {}),
      ...(groupLabel ? { groupLabel } : {}),
      ...(sectionLabel ? { sectionLabel } : {}),
      ...(filterLabel ? { filterLabel } : {})
    });
  } finally {
    selfUpdate = false;
  }
};

const onSelect = (e: Event) => {
  selectedOption.value = (e.target as HTMLSelectElement).value;
  if (selectedOption.value !== RelativeDateRangeOption.SPECIFIC_RANGE) {
    fromDate.value = "";
    toDate.value = "";
  }
  if (selectedOption.value !== RelativeDateRangeOption.CUSTOM_DAYS) customDays.value = "";
  commit();
};

const onDays = (e: Event) => {
  const raw = (e.target as HTMLInputElement).value;
  customDays.value = raw === "" || raw === undefined ? "" : (clampPlusMinusDays(Number(raw)) ?? "");
  commit();
};

const onFrom = (e: Event) => {
  fromDate.value = (e.target as HTMLInputElement).value ?? "";
  commit();
};

const onTo = (e: Event) => {
  toDate.value = (e.target as HTMLInputElement).value ?? "";
  commit();
};
</script>

<template>
  <div class="relative-daterange-filter" :data-test="`relative-daterange-filter/${filterKey}`">
    <mc-select
      :key="locale"
      fit="small"
      :name="filterKey"
      :label="t(filterLabel)"
      :placeholder="t(filter.placeholder ?? 'ORDERS.FILTERS.RELATIVE_DATE.PLACEHOLDER')"
      :value="selectedOption"
      :disabled="disabled || undefined"
      :data-test="`relative-daterange-filter/${filterKey}/select`"
      @input="onSelect"
    >
      <mc-option v-for="option in options" :key="option.value" :value="option.value">
        {{ t(option.label) }}
      </mc-option>
    </mc-select>

    <mc-number-stepper
      v-if="selectedOption === RelativeDateRangeOption.CUSTOM_DAYS"
      fit="small"
      min="0"
      :max="MAX_PLUS_MINUS_DAYS"
      step="1"
      :label="t('ORDERS.FILTERS.RELATIVE_DATE.DAYS_FROM_TODAY')"
      :value="customDays === '' ? undefined : customDays"
      :disabled="disabled || undefined"
      :data-test="`relative-daterange-filter/${filterKey}/days`"
      @input="onDays"
    />

    <div v-if="selectedOption === RelativeDateRangeOption.SPECIFIC_RANGE" class="relative-daterange-filter__range">
      <mc-input-date
        fit="small"
        name="from-date"
        :label="t('FROM')"
        :value="fromDate"
        :max="toDate || undefined"
        :disabled="disabled || undefined"
        :data-test="`relative-daterange-filter/${filterKey}/from`"
        @input="onFrom"
      />
      <mc-input-date
        fit="small"
        name="to-date"
        :label="t('TO')"
        :value="toDate"
        :min="fromDate || undefined"
        :disabled="disabled || undefined"
        :data-test="`relative-daterange-filter/${filterKey}/to`"
        @input="onTo"
      />
    </div>

    <p
      v-if="hint"
      class="relative-daterange-filter__hint mds-text--x-small"
      :data-test="`relative-daterange-filter/${filterKey}/hint`"
    >
      {{ hint }}
    </p>
  </div>
</template>

<style scoped lang="scss">
.relative-daterange-filter {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__range {
    display: flex;
    gap: 8px;
  }

  &__hint {
    margin: 0;
    color: var(--mds_brand_appearance_neutral_weak_text-color);
  }
}
</style>
